import React, { useCallback, useContext, useEffect, useRef, useState, useMemo } from 'react'
import moment from 'moment'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import cn from 'clsx'
import styled from 'styled-components'
import OfferCalendarMonthPicker from '../MonthPicker/OfferCalendarMonthPicker'
import OfferCalendarDayPicker from '../DayPicker/OfferCalendarDayPicker'
import { rem } from 'polished'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import { OfferPackageOptionView, getComplementaryBundleState, getAvailableBundlePackages, BundleOfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import { getInteractionStudioDataFromOfferPage } from 'selectors/interactionStudioSelectors'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions, OfferPageFilterTypes } from 'contexts/OfferPage/offerPageStateReducer'
import OfferCalendarNotAvailableMessage from '../OfferCalendarNotAvailableMessage'
import OfferCalendarNoFlightsMessage from '../OfferCalendarNoFlightsMessage'
import { CALENDAR_GA_EVENT_ACTION, CALENDAR_GA_EVENT_LABEL } from 'constants/analytics'
import CloseButton from 'components/Common/CloseButton'
import Heading from 'components/Luxkit/Typography/Heading'
import LineArrowLeftIcon from 'components/Luxkit/Icons/line/LineArrowLeftIcon'
import OfferCalendarDurationSelect from '../CalendarDuration/OfferCalendarDurationSelect'
import useHasMounted from 'hooks/useHasMounted'
import BodyText from 'components/Luxkit/Typography/BodyText'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'

import CalendarPickerRecommendations from '../Recommendations/CalendarPickerRecommendations'
import CalendarPickerRecommendationsHeading from '../Recommendations/CalendarPickerRecommendationsHeading'
import { shouldEnableCalendarRecommendations } from 'selectors/featuresSelectors'
import ErrorBoundary from 'components/Common/ErrorBoundary'
import IconButton from 'components/Luxkit/Button/IconButton'
import {
  isMultipleRoomBookingActive,
  getCalendarMonths,
  getSelectedOfferPageDay,
  isFetchingCalendars,
  getDaysOtherAvailability,
  getPackagesForFilters,
} from 'selectors/multipleRoomBookingSelectors'
import { PaginatorState, BundledOfferState, OfferPageState, takeCurrentOfferState } from 'contexts/OfferPage/offerPageStateReducer'
import Group from 'components/utils/Group'
import OfferCalendarBundlePackageSelect from '../CalendarBundlePackages/OfferCalendarBundlePackageSelect'
import OfferBundleAndSaveCalendarDatePickerFooter from './OfferBundleAndSaveCalendarDatePickerFooter'
import { isBundleAndSaveV2Enabled } from 'lib/offer/offerUtils'

const FadingBackButton = styled(IconButton)`
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s;

  &.visible {
    opacity: 1;
    pointer-events: auto;
  }
`

const Calendar = styled.div`
  padding: ${rem(20)} ${rem(40)};
  flex-grow: 1;
  min-height: ${rem(400)};
`

const Header = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${rem(32)} ${rem(40)};
  box-shadow: ${props => props.theme.shadow.bottom.small};
  background-color: ${props => props.theme.palette.neutral.default.eight};
  z-index: 2;
  border-top-left-radius: ${props => props.theme.borderRadius.M};
  border-top-right-radius: ${props => props.theme.borderRadius.M};
`

const RecommendationHeadingWrapper = styled.div`
  flex-grow: 1;
  display: flex;
  justify-content: flex-start;
  align-items: center;
`

const Content = styled.div`
  position: relative;
  overflow: hidden;
  border-bottom-left-radius: ${props => props.theme.borderRadius.M};
  border-bottom-right-radius: ${props => props.theme.borderRadius.M};
  display: flex;
  flex-direction: column;
`

const FetchingCalendarsIndicator = styled(LoadingIndicator)`
  padding: 0;
`

const Scroller = styled.div`
  overflow: auto;
`

interface Props {
  offer: App.Offer;
  airport: App.Airport;
  checkIn?: moment.Moment;
  fetching?: boolean;
  selectedDay?: App.CalendarDay;
  flights?: boolean;
  calendarError?: boolean;
  calendarAvailability: boolean;
  useRooms?: boolean;
  defaultDayPicker?: boolean;
  defaultMonth?: string;
  relevantPackages: Array<OfferPackageOptionView>;
  selectedPackageView: OfferPackageOptionView;
  recommendationsShown: boolean;
  rooms?: App.Occupants[];
  otherAvailability: { [checkIn: string]: boolean };
  selectedPackage: App.Package,
  interactionStudioData?: App.ISACheckoutData;
  filter: OfferPageFilterTypes;
  isMultipleRoomBookingActive: boolean;
  paginator?: PaginatorState;
  bundledOffersState: OfferPageState['bundledOffers'];
  complementaryBundleState?: BundledOfferState
  filteredBundlePackageId?: string
  availableBundlePackages?: { [bundlePackageId: string]: BundleOfferPackageOptionView }
  isCalendarAlreadyOpen?: boolean
}

function OfferBundleAndSaveCalendarDatePicker(props: Props) {
  const {
    checkIn,
    offer,
    fetching,
    selectedDay,
    flights,
    calendarError,
    calendarAvailability,
    defaultMonth,
    defaultDayPicker,
    relevantPackages,
    selectedPackageView,
    otherAvailability,
    rooms,
    recommendationsShown,
    interactionStudioData,
    filter,
    isMultipleRoomBookingActive,
    paginator,
    bundledOffersState,
    complementaryBundleState,
    filteredBundlePackageId,
    availableBundlePackages,
    isCalendarAlreadyOpen,
  } = props

  const onUserAnalyticsAction = useContext(UserAnalyticsActionContext)
  const [monthIndex, setMonth] = useState<string | undefined>(defaultMonth ?? selectedDay?.monthKey)
  const dispatch = useOfferPageDispatch()
  const mounted = useHasMounted()

  const bundledOffers = useMemo(() => Object.values(bundledOffersState ?? {}).filter(Boolean), [bundledOffersState])

  useEffect(() => {
    if (!fetching && checkIn && selectedDay && !selectedDay.hotelAvailable && !otherAvailability[selectedDay.checkIn]) {
      // clear check in if the date we have selected is no longer available
      dispatch({ type: OfferPageActions.setCheckInDate, checkIn: undefined })
    }
  }, [checkIn, selectedDay]) // eslint-disable-line react-hooks/exhaustive-deps

  const onMonthSelect = useCallback((monthKey: string) => {
    setMonth(monthKey)
  }, [])

  const onDropdownClose = useCallback(() => {
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.CloseFilter, CALENDAR_GA_EVENT_LABEL.CloseCross)
    dispatch({ type: OfferPageActions.closeFilter })
  }, [dispatch, onUserAnalyticsAction])

  const onBackClick = useCallback(() => {
    if (recommendationsShown) {
      dispatch({ type: OfferPageActions.hideRecommendationsView })
      return
    }

    if (paginator?.backOfferId) {
      dispatch({ type: OfferPageActions.setPaginatorCurrentOfferId, currentOfferId: paginator?.backOfferId })
      return
    }

    setMonth(undefined)
  }, [setMonth, dispatch, recommendationsShown, paginator])

  const onBackToMonth = useCallback(() => {
    if (recommendationsShown) {
      dispatch({ type: OfferPageActions.hideRecommendationsView })
      return
    }
    dispatch({ type: OfferPageActions.resetDates, offerId: paginator?.currentOfferId })
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.YearView)
    setMonth(undefined)
  }, [dispatch, onUserAnalyticsAction, paginator, recommendationsShown])

  const containerRef = useRef<HTMLDivElement>(null)
  const currentOfferIndex = bundledOffers.findIndex((o) => o.offer.id === paginator?.currentOfferId)

  return (<>
    <Header>
      {!recommendationsShown && (
        <Group direction="horizontal" horizontalAlign="space-between">
          <Group direction="horizontal" gap={16}>
            {monthIndex && (
              <FadingBackButton
                kind="tertiary"
                className={cn({ visible: monthIndex !== undefined || recommendationsShown })}
                size="small"
                onClick={onBackClick}
              >
                <LineArrowLeftIcon />
              </FadingBackButton>)}
            <Group direction="vertical" gap={4}>
              <BodyText className={cn({ visible: monthIndex !== undefined || recommendationsShown })} variant="medium">
                {isBundleAndSaveV2Enabled() ? 'Select your dates' : `Select dates for hotel ${currentOfferIndex + 1}`}
              </BodyText>
              <Heading variant="heading5" lineClamp={1}>
                {bundledOffers[currentOfferIndex].offer?.property?.name ?? bundledOffers[currentOfferIndex].offer?.name}
              </Heading>
              {filteredBundlePackageId && availableBundlePackages?.[filteredBundlePackageId] && <Heading variant="heading6" lineClamp={1}>
                {availableBundlePackages?.[filteredBundlePackageId].bundlePackageName}
              </Heading>}
            </Group>
          </Group>
          <Group direction="horizontal" gap={12}>
            <Group direction="horizontal" verticalAlign="end" gap={8}>
              {fetching && <BodyText variant="medium" weight="bold">Finding availability</BodyText>}
              <FetchingCalendarsIndicator inline visible={fetching} />
              {!isBundleAndSaveV2Enabled() && <OfferCalendarDurationSelect />}
              <OfferCalendarBundlePackageSelect />
            </Group>
            <Group direction="horizontal" verticalAlign="start">
              <CloseButton size="small" onClick={onDropdownClose} />
            </Group>
          </Group>
        </Group>
      )}
      {recommendationsShown &&
        <Group direction="horizontal" verticalAlign="center" horizontalAlign="space-between">
          <FadingBackButton
            kind="tertiary"
            className={cn({ visible: true })}
            outdent="left"
            size="large"
            onClick={onBackToMonth}
          >
            <LineArrowLeftIcon />
          </FadingBackButton>
          <RecommendationHeadingWrapper>
            <ErrorBoundary fallback={null}>
              <CalendarPickerRecommendationsHeading />
            </ErrorBoundary>
          </RecommendationHeadingWrapper>
          <CloseButton size="small" onClick={onDropdownClose} />
        </Group>}
    </Header>
    <Content>
      <Scroller ref={containerRef}>
        <Calendar>
          {recommendationsShown && (
            <CalendarPickerRecommendations
              offerId={offer.id}
              offerType={offer.type}
              rooms={rooms}
              heading={`Similar hotels to ${offer.property?.name ?? offer?.location}`}
              seedOffer={offer}
            />
          )}
          {!recommendationsShown && (
            <>
              {mounted && !fetching && !calendarAvailability && <>
                {(calendarError || !flights) && <OfferCalendarNotAvailableMessage hasError={calendarError} />}
                {!calendarError && flights && <OfferCalendarNoFlightsMessage canSwitchToHotel={!offer?.bundledWithFlightsOnly} />}
              </>}
              {calendarAvailability && <>
                {monthIndex !== undefined && !fetching && <OfferCalendarDayPicker
                  onBackToMonth={onBackToMonth}
                  initialMonth={monthIndex}
                  defaultDayPicker={defaultDayPicker}
                  flightsOnly={offer.bundledWithFlightsOnly}
                  contentRef={containerRef}
                />}
                {monthIndex === undefined && <OfferCalendarMonthPicker
                  onMonthSelect={onMonthSelect}
                />}
              </>
              }
            </>)
          }
        </Calendar>
      </Scroller>
      <OfferBundleAndSaveCalendarDatePickerFooter
        checkIn={checkIn}
        offer={offer}
        fetching={fetching}
        selectedDay={selectedDay}
        relevantPackages={relevantPackages}
        selectedPackageView={selectedPackageView}
        otherAvailability={otherAvailability}
        interactionStudioData={interactionStudioData}
        filter={filter}
        isMultipleRoomBookingActive={isMultipleRoomBookingActive}
        paginator={paginator}
        complementaryBundleState={complementaryBundleState}
        isCalendarAlreadyOpen={isCalendarAlreadyOpen}
      />
    </Content>
  </>
  )
}

export default withOfferPageAndReduxState((currentState, appState) => {
  const state = takeCurrentOfferState(currentState)

  return {
    filteredBundlePackageId: currentState.filteredBundlePackageId,
    availableBundlePackages: getAvailableBundlePackages(currentState, appState),
    bundledOffersState: state.bundledOffers,
    paginator: state.paginator,
    checkIn: state.checkInDate,
    offer: state.offer,
    airport: state.airport,
    recommendationsShown: state.showRecommendationsView && shouldEnableCalendarRecommendations(appState, state.offer),
    relevantPackages: getPackagesForFilters(state, appState),
    selectedPackageView: state.selectedPackageView,
    rooms: state.rooms,
    otherAvailability: getDaysOtherAvailability(state, appState),
    selectedDay: getSelectedOfferPageDay(state, appState),
    fetching: isFetchingCalendars(state, appState),
    calendarAvailability: getCalendarMonths(currentState, appState).availableCount > 0,
    calendarError: state.offer.packages.some(pkg => appState.calendar.calendarErrors[pkg.uniqueKey]),
    selectedPackage: state.package,
    interactionStudioData: getInteractionStudioDataFromOfferPage(appState, state),
    filter: state.activeFilter,
    isMultipleRoomBookingActive: isMultipleRoomBookingActive(state, appState),
    complementaryBundleState: getComplementaryBundleState(currentState),
  }
})(OfferBundleAndSaveCalendarDatePicker)
